import { useEffect } from "react";
import { useSiteContent } from "@/hooks/useSiteContent";

const loadScriptDeferred = (id: string, innerHTML: string, appendTo: "head" | "body" = "head") => {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.innerHTML = innerHTML;
  (appendTo === "body" ? document.body : document.head).appendChild(script);
};

const loadExternalScriptDeferred = (id: string, src: string) => {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

/**
 * Inject a raw HTML snippet (as pasted from GTM) into either <head> or <body>.
 * Re-creates <script> and <noscript> elements so they execute / render correctly,
 * since innerHTML alone does NOT execute scripts.
 * Idempotent: a marker container with `containerId` ensures we never inject twice.
 */
const injectRawSnippet = (
  containerId: string,
  rawHtml: string,
  target: "head" | "body" = "head",
  position: "prepend" | "append" = "append",
) => {
  if (typeof document === "undefined") return;
  if (!rawHtml || !rawHtml.trim()) return;
  if (document.getElementById(containerId)) return;

  const wrapper = document.createElement("div");
  wrapper.id = containerId;
  wrapper.style.display = "none";
  wrapper.setAttribute("data-injected-snippet", "true");

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${rawHtml}</div>`, "text/html");
  const root = doc.body.firstChild as HTMLElement | null;
  if (!root) return;

  const targetEl = target === "head" ? document.head : document.body;
  const placement = position === "prepend" ? "afterbegin" : "beforeend";

  // Append marker first so it lives in the DOM
  if (placement === "afterbegin") {
    targetEl.insertBefore(wrapper, targetEl.firstChild);
  } else {
    targetEl.appendChild(wrapper);
  }

  // Walk children and inject; <script> needs re-creation to execute
  Array.from(root.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();
      if (tag === "script") {
        const s = document.createElement("script");
        for (const attr of Array.from(el.attributes)) s.setAttribute(attr.name, attr.value);
        if (el.textContent) s.text = el.textContent;
        // Inject scripts into the actual target (not inside the hidden wrapper) so they execute in document context
        targetEl.appendChild(s);
      } else {
        // noscript / iframe / divs etc. — keep inside wrapper
        wrapper.appendChild(el.cloneNode(true));
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      // ignore whitespace text
    }
  });
};

const SeoHead = () => {
  const { content, loading } = useSiteContent();

  useEffect(() => {
    if (loading) return;

    const get = (key: string) => content[key] || "";

    const seoTitle = get("seo_title");
    const seoDescription = get("seo_description");
    const seoKeywords = get("seo_keywords");
    const seoCanonicalUrl = get("seo_canonical_url");
    const seoOgTitle = get("seo_og_title") || seoTitle;
    const seoOgDescription = get("seo_og_description") || seoDescription;
    const seoOgImage = get("seo_og_image");
    const seoRobots = get("seo_robots") || "index, follow";
    const seoGoogleVerification = get("seo_google_verification");
    const seoBingVerification = get("seo_bing_verification");
    const fbPixelId = get("fb_pixel_id");
    const fbPixelEnabled = get("fb_pixel_enabled") === "true";
    const gtmId = get("gtm_id");
    const gtmEnabled = get("gtm_enabled") === "true";
    const gtmHeadCode = get("gtm_head_code");
    const gtmBodyCode = get("gtm_body_code");
    const ga4Id = get("ga4_measurement_id");
    const ga4Enabled = get("ga4_enabled") === "true";
    const clarityId = get("clarity_id");
    const clarityEnabled = get("clarity_enabled") === "true";
    const faviconUrl = get("favicon_url");

    if (seoTitle) document.title = seoTitle;

    const setMeta = (name: string, c: string, attr: "name" | "property" = "name") => {
      if (!c) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", c);
    };

    setMeta("description", seoDescription);
    setMeta("keywords", seoKeywords);
    setMeta("robots", seoRobots);
    setMeta("og:title", seoOgTitle, "property");
    setMeta("og:description", seoOgDescription, "property");
    setMeta("og:image", seoOgImage, "property");
    setMeta("og:type", "website", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", seoOgTitle);
    setMeta("twitter:description", seoOgDescription);
    setMeta("twitter:image", seoOgImage);

    if (seoGoogleVerification) setMeta("google-site-verification", seoGoogleVerification);
    if (seoBingVerification) setMeta("msvalidate.01", seoBingVerification);

    // Dynamic Favicon
    const dynamicFavicon = faviconUrl || get("logo_url");
    if (dynamicFavicon) {
      let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "icon");
        document.head.appendChild(link);
      }
      link.setAttribute("href", dynamicFavicon);
      link.setAttribute("type", "image/png");

      let appleLink = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
      if (!appleLink) {
        appleLink = document.createElement("link");
        appleLink.setAttribute("rel", "apple-touch-icon");
        document.head.appendChild(appleLink);
      }
      appleLink.setAttribute("href", dynamicFavicon);
    }

    // Canonical URL
    if (seoCanonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", seoCanonicalUrl);
    }

    if (ga4Id && ga4Enabled) {
      loadExternalScriptDeferred("ga4-gtag", `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`);
      loadScriptDeferred("ga4-script", `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${ga4Id}');
      `);
    }

    if (fbPixelId && fbPixelEnabled) {
      if (typeof window !== "undefined" && typeof (window as any).__ffInitFbPixel === "function") {
        (window as any).__ffInitFbPixel(fbPixelId);
      } else {
        loadScriptDeferred("fb-pixel-script", `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${fbPixelId}');
        fbq('track', 'PageView');
      `);
      }
    }

    if (gtmId && gtmEnabled) {
      loadScriptDeferred("gtm-script", `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');
      `);
    }

    // GTM Custom Snippets — pasted directly from Google Tag Manager UI.
    // These are independent of gtm_id/gtm_enabled and do NOT touch Facebook Pixel.
    if (gtmHeadCode && gtmHeadCode.trim()) {
      injectRawSnippet("ff-gtm-head-snippet", gtmHeadCode, "head", "append");
    }
    if (gtmBodyCode && gtmBodyCode.trim()) {
      injectRawSnippet("ff-gtm-body-snippet", gtmBodyCode, "body", "prepend");
    }

    // Microsoft Clarity
    if (clarityId && clarityEnabled) {
      loadScriptDeferred("clarity-script", `
        (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window,document,"clarity","script","${clarityId}");
      `);
    }

  }, [content, loading]);

  return null;
};

export default SeoHead;
